import { useSearchParams } from 'react-router-dom';
import {
    Badge, Button, Card, EmptyState, IconSearch, LoadingList, PageHeader, Table, Td, Th, cx, fieldClass, filterLabelClass, useIsPhone,
} from '@timely/ui';
import {
    EMPTY_STOCK_FILTERS, holds, matchesStockFilters, stockAvailability, summariseStock,
    type StockAvailability, type StockFilters, type StockItem,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { useStock } from '../stock/useStock';
import { StockConnect } from '../stock/StockConnect';

/**
 * Stock.
 *
 * A live read of a company's own stock sheet, exactly as the reference app
 * did it and for the same reason: a CRM on a call needs to know what is on
 * the shelf as much as a manager does, so `stock.view` is on every default
 * role including Collector and Viewer. What changes per role is **price**,
 * and only price — `stock.view.prices` decides whether `rate`/`value` were
 * ever in the response this screen received, redacted server-side before it
 * arrived, never hidden by a check in this file.
 *
 * Search, category and availability all live in this screen's own URL, the
 * same pattern the cheque register uses — a term typed here cannot bleed into
 * the book's search or be emptied by leaving the tab.
 */
export function Stock() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count } = useFormatters();
    const isPhone = useIsPhone();

    const canManage = holds(context, 'datasource.manage');
    const [params, setParams] = useSearchParams();
    const filters = filtersFromParams(params);

    const { configured, items, priced, truncated, fetchedAt, statusDetail, loading, error, refresh } =
        useStock(workspace.id);

    const set = (changes: Partial<StockFilters>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            if (value === '' || value === 'all') next.delete(key); else next.set(key, String(value));
        }
        setParams(next, { replace: true });
    };

    const summary = summariseStock(items);
    const visible = items.filter(item => matchesStockFilters(item, filters));

    return (
        <Page width="wide">
            <PageHeader
                title="Stock"
                subtitle="Read from your stock sheet each time you open this. Nothing here is imported or stored."
                actions={
                    configured ? (
                        <Button variant="secondary" disabled={loading} onClick={() => { void refresh(); }} busy={loading}>
                            {loading ? 'Reading…' : 'Read again'}
                        </Button>
                    ) : undefined
                }
            />

            {error && <div className="mb-4"><FormNotice tone="error">{error}</FormNotice></div>}
            {truncated && (
                <div className="mb-4">
                    <FormNotice tone="error">
                        This sheet has more items than one read can show. Showing the first ones only.
                    </FormNotice>
                </div>
            )}

            {loading ? (
                <Card className="overflow-hidden"><LoadingList label="Reading your stock sheet" rows={6} /></Card>
            ) : !configured ? (
                <StockConnect workspaceId={workspace.id} canManage={canManage} statusDetail={statusDetail} onConnected={() => { void refresh(); }} />
            ) : (
                <Card className="overflow-hidden">
                    <SummaryStrip
                        summary={summary}
                        active={filters.availability}
                        onPick={a => set({ availability: a === filters.availability ? 'all' : a })}
                    />

                    <FilterBar
                        filters={filters}
                        categories={summary.categories}
                        onChange={set}
                        onClear={() => setParams(new URLSearchParams(), { replace: true })}
                    />

                    <div className="px-4 py-2 border-b border-separator flex items-baseline gap-x-4 gap-y-1 flex-wrap text-caption text-label-3">
                        {fetchedAt && <span>Read {agoText(fetchedAt)}</span>}
                        {visible.length < items.length && <span>Showing {count(visible.length)} of {count(items.length)}</span>}
                        {priced && summary.totalValue != null
                            ? <span>Total value <strong className="text-body font-semibold text-label num">{money.compact(summary.totalValue)}</strong></span>
                            : !priced && <span>Prices are not shown for your role</span>}
                    </div>

                    {items.length === 0 ? (
                        <EmptyState
                            title="Nothing on the sheet"
                            hint="Your stock sheet was read successfully, but it has no items in it yet."
                        />
                    ) : visible.length === 0 ? (
                        <EmptyState
                            title="Nothing matches"
                            hint={`None of your ${count(items.length)} items match what you are looking for.`}
                            action={
                                <Button variant="secondary" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                                    Clear filters
                                </Button>
                            }
                        />
                    ) : isPhone ? (
                        <ul className="divide-y divide-separator">
                            {visible.map(item => <StockCard key={item.id} item={item} money={priced ? money : null} />)}
                        </ul>
                    ) : (
                        <Table caption="Items on the stock sheet, with quantity and availability.">
                            <thead>
                                <tr>
                                    <Th>Item</Th>
                                    <Th hideBelow="md" className="w-48">Category</Th>
                                    <Th numeric className="w-32">Quantity</Th>
                                    <Th className="w-36">Availability</Th>
                                    {priced && <Th numeric className="w-32">Rate</Th>}
                                    {priced && <Th numeric className="w-36">Value</Th>}
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(item => (
                                    <StockRow key={item.id} item={item} money={priced ? money : null} />
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card>
            )}
        </Page>
    );
}

/* --------------------------------- summary --------------------------------- */

const AVAILABILITY_LABEL: Record<StockAvailability, string> = { in: 'In stock', low: 'Low', out: 'Out of stock' };
const AVAILABILITY_TONE: Record<StockAvailability, 'pos' | 'warn' | 'dang'> = { in: 'pos', low: 'warn', out: 'dang' };

/** The count of every item, and the three availabilities as filters — one strip, divided by hairlines. */
function SummaryStrip({
    summary, active, onPick,
}: {
    summary: ReturnType<typeof summariseStock>;
    active: StockFilters['availability'];
    onPick: (a: StockAvailability) => void;
}) {
    const cells: { key: StockAvailability | 'total'; label: string; value: number }[] = [
        { key: 'total', label: 'All items', value: summary.total },
        { key: 'in', label: 'In stock', value: summary.inStock },
        { key: 'low', label: 'Low', value: summary.low },
        { key: 'out', label: 'Out of stock', value: summary.out },
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-separator border-b border-separator">
            {cells.map(cell => {
                const on = cell.key !== 'total' && active === cell.key;
                const content = (
                    <>
                        <span className={cx('text-secondary block', on ? 'font-semibold text-accent-text' : 'font-medium text-label-2')}>{cell.label}</span>
                        <span className="text-heading font-semibold text-label num block mt-1">{cell.value}</span>
                    </>
                );
                return cell.key === 'total' ? (
                    <div key={cell.key} className="bg-card px-4 py-3">{content}</div>
                ) : (
                    <button
                        key={cell.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onPick(cell.key as StockAvailability)}
                        className={cx(
                            'text-left px-4 py-3 transition-colors duration-100 relative',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                            on ? 'bg-accent-tint after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-accent' : 'bg-card hover:bg-hover',
                        )}
                    >
                        {content}
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------- filters ---------------------------------- */

function FilterBar({
    filters, categories, onChange, onClear,
}: {
    filters: StockFilters;
    categories: string[];
    onChange: (changes: Partial<StockFilters>) => void;
    onClear: () => void;
}) {
    const narrowed = (filters.search ? 1 : 0) + (filters.category !== 'all' ? 1 : 0) + (filters.availability !== 'all' ? 1 : 0);

    return (
        <div className="px-4 py-3 border-b border-separator flex flex-wrap items-end gap-2.5">
            <div className="flex-1 min-w-48 md:max-w-sm">
                <label htmlFor="stock-search" className={filterLabelClass}>Search</label>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-label-3 pointer-events-none"><IconSearch /></span>
                    <input
                        id="stock-search"
                        type="search"
                        value={filters.search}
                        onChange={e => onChange({ search: e.target.value })}
                        placeholder="Item, code or category"
                        className={fieldClass({ className: cx('w-full pl-9', filters.search && '!border-accent bg-accent-tint') })}
                    />
                </div>
            </div>
            {categories.length > 0 && (
                <div>
                    <label htmlFor="stock-category" className={filterLabelClass}>Category</label>
                    <select
                        id="stock-category"
                        value={filters.category}
                        onChange={e => onChange({ category: e.target.value })}
                        className={fieldClass({ className: cx('min-w-40', filters.category !== 'all' && '!border-accent bg-accent-tint') })}
                    >
                        <option value="all">Every category</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
            )}
            {narrowed > 0 && (
                <Button variant="quiet" onClick={onClear}>Clear all</Button>
            )}
        </div>
    );
}

/* ---------------------------------- rows ----------------------------------- */

function StockRow({ item, money }: { item: StockItem; money: ReturnType<typeof useFormatters>['money'] | null }) {
    const availability = stockAvailability(item);
    return (
        <tr className="hover:bg-hover">
            <Td>
                <span className="block font-semibold text-label truncate">{item.name}</span>
                {item.code && <span className="block text-caption text-label-3">{item.code}</span>}
            </Td>
            <Td hideBelow="md" className="text-label-2 truncate">{item.category ?? '—'}</Td>
            <Td numeric>
                {item.quantity ?? '—'}{item.unit && item.quantity != null && <span className="text-label-3"> {item.unit}</span>}
            </Td>
            <Td><Badge tone={AVAILABILITY_TONE[availability]}>{AVAILABILITY_LABEL[availability]}</Badge></Td>
            {money && <Td numeric>{item.rate != null ? money.compact(item.rate) : '—'}</Td>}
            {money && <Td numeric className="font-semibold">{item.value != null ? money.compact(item.value) : '—'}</Td>}
        </tr>
    );
}

function StockCard({ item, money }: { item: StockItem; money: ReturnType<typeof useFormatters>['money'] | null }) {
    const availability = stockAvailability(item);
    return (
        <li className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="font-semibold text-label truncate">{item.name}</p>
                    <p className="text-caption text-label-3">
                        {[item.code, item.category].filter(Boolean).join(' · ') || '—'}
                    </p>
                </div>
                <Badge tone={AVAILABILITY_TONE[availability]}>{AVAILABILITY_LABEL[availability]}</Badge>
            </div>
            <div className="mt-2 flex items-baseline justify-between text-secondary">
                <span className="num">
                    {item.quantity ?? '—'}{item.unit && item.quantity != null && ` ${item.unit}`}
                </span>
                {money && item.value != null && (
                    <span className="num font-semibold">{money.compact(item.value)}</span>
                )}
            </div>
        </li>
    );
}

/* -------------------------------- helpers ----------------------------------- */

const agoText = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return new Date(iso).toLocaleDateString();
};

function filtersFromParams(params: URLSearchParams): StockFilters {
    const availability = params.get('availability');
    return {
        search: params.get('search') ?? EMPTY_STOCK_FILTERS.search,
        category: params.get('category') ?? EMPTY_STOCK_FILTERS.category,
        availability: availability === 'in' || availability === 'low' || availability === 'out' ? availability : 'all',
    };
}
